import { useCallback, useRef, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import DOMPurify from 'dompurify';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, AlignmentType, HeadingLevel, Footer, ImageRun, BorderStyle, ShadingType, VerticalAlign } from 'docx';
import { saveAs } from 'file-saver';

// Fallback to main thread if Web Workers unavailable
const isWorkerSupported = typeof Worker !== 'undefined';

// Inline SVG logo for PDF embedding - the actual Amplify HR logo
const LOGO_SVG = `<svg width="44" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 216 200">
  <path fill="#3c68ff" d="M 216 186.68 Q 216 186.83 216 186.99 L 199.96 200 L 198.33 200 L 96.05 2.58 A 0.23 0.23 0 0 1 96.25 2.25 L 119.68 2.25 A 0.93 0.92 -13.8 0 1 120.5 2.74 L 216 186.68 Z"/>
  <path fill="#3c68ff" d="M 17.97 200 L 16.09 200 L 0.35 187.26 A 0.48 0.47 32.7 0 1 0.22 186.68 L 92.99 7.65 A 0.4 0.4 0 0 1 93.7 7.65 L 105.41 30.26 A 0.71 0.69 45.8 0 1 105.41 30.91 L 17.97 200 Z"/>
  <path fill="#3c68ff" d="M 108 132.96 Q 108.23 132.96 108.26 132.98 Q 148.87 151.44 173.81 162.84 A 1.2 1.2 0 0 1 174.37 163.37 L 192.01 197.43 A 0.29 0.28 -1.8 0 1 191.64 197.82 Q 157.51 182.34 108.23 159.83 Q 108.22 159.82 108 159.82 Q 107.78 159.82 107.77 159.83 Q 58.49 182.35 24.37 197.83 A 0.29 0.28 1.8 0 1 24 197.44 L 41.63 163.38 A 1.2 1.2 0 0 1 42.19 162.85 Q 67.13 151.45 107.74 132.98 Q 107.77 132.96 108 132.96 Z"/>
</svg>`;

// Currency formatter (for main thread fallback)
const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));

// HTML escape function to prevent XSS
const escapeHtml = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Helper functions to eliminate nested ternaries
const getSavingsClass = (amount) => {
  if (amount > 0) return 'positive';
  if (amount < 0) return 'negative';
  return '';
};

const formatSavings = (amount, fmt) => {
  return amount !== 0 ? fmt(amount) : '—';
};

const getCurrentAdminDisplay = (isBundled, isAdp, adpUnbundled, wcPremium, currentAdmin, svcFeeAmt) => {
  if (isBundled) return wcPremium + currentAdmin;
  if (isAdp && adpUnbundled) return svcFeeAmt;
  return currentAdmin;
};

const getCurrentAdminTotal = (isBundled, isAdp, adpUnbundled, wcPremiumTotal, adminFee, totalServiceFees) => {
  if (isBundled) return wcPremiumTotal + adminFee;
  if (isAdp && adpUnbundled) return totalServiceFees;
  return adminFee;
};

const getAdminSavings = (isBundled, isAdp, adpUnbundled, currentWcPremium, currentAdmin, proposedAdmin, svcFeeAmt) => {
  if (isBundled) return (currentWcPremium + currentAdmin) - proposedAdmin;
  if (isAdp && adpUnbundled) return svcFeeAmt - proposedAdmin;
  return currentAdmin - proposedAdmin;
};

// Calculate function (for main thread fallback)
const calculate = (item, sutaBases) => {
  const wageCap = sutaBases[item.state] || 0;
  const ee = item.employeeCount || 1;
  const avgWage = item.grossWages / ee;
  const taxablePerEE = Math.min(avgWage, wageCap);
  const totalTaxableWages = taxablePerEE * ee;
  const sutaTax = totalTaxableWages * (item.sutaRate / 100);
  const wcPremium = (item.grossWages / 100) * item.wcRate;
  return { wageCap, avgWage, totalTaxableWages, sutaTax, wcPremium };
};

// Professional PDF HTML template
function generatePdfHtml(data) {
  const {
    lineItems, proposedItems, sutaBases, sutaRates, currentTotals, proposedTotals,
    currentAdminFee, proposedAdminFee, currentTotal, proposedTotal,
    savings, savingsPct, isAdp, adpUnbundled, serviceFees, totalServiceFees,
    clientName, currentProvider, preparedBy, quoteDate, MONOPOLISTIC_STATES,
  } = data;

  const statesList = [...new Set(lineItems.map(i => i.state))].sort().join(", ");
  const isBundled = isAdp && !adpUnbundled;
  
  // Helper to check if state is client reporting
  const isClientState = (state) => sutaRates && sutaRates[state]?.bill === null;
  const isMonopolistic = (state) => MONOPOLISTIC_STATES && MONOPOLISTIC_STATES.includes(state);
  
  // Check if we have any special states
  const hasClientStates = lineItems.some(i => isClientState(i.state));
  const hasMonopolisticStates = lineItems.some(i => isMonopolistic(i.state));
  
  const lineItemRows = lineItems.map(item => {
    const c = calculate(item, sutaBases);
    const p = proposedItems.find(pi => pi.id === item.id);
    const pc = calculate(p, sutaBases);
    const currentAdminPerItem = currentTotals.employees > 0 ? (currentAdminFee * item.employeeCount / currentTotals.employees) : 0;
    const proposedAdminPerItem = proposedTotals.employees > 0 ? (proposedAdminFee * item.employeeCount / proposedTotals.employees) : 0;
    const svcFeeRate = serviceFees && serviceFees[item.id] ? serviceFees[item.id].rate || 0 : 0;
    const svcFeeAmt = (item.grossWages / 100) * svcFeeRate;
    const currentAdminDisplay = getCurrentAdminDisplay(isBundled, isAdp, adpUnbundled, c.wcPremium, currentAdminPerItem, svcFeeAmt);
    const wcSave = c.wcPremium - pc.wcPremium;
    const sutaSave = c.sutaTax - pc.sutaTax;
    const adminSave = getAdminSavings(isBundled, isAdp, adpUnbundled, c.wcPremium, currentAdminPerItem, proposedAdminPerItem, svcFeeAmt);
    
    // State badges
    const clientBadge = isClientState(item.state) ? '<span class="state-badge client">C</span>' : '';
    const monoBadge = isMonopolistic(item.state) ? '<span class="state-badge mono">M</span>' : '';
    
    return `<tr>
      <td>${escapeHtml(item.state)} ${clientBadge}${monoBadge}</td>
      <td>${escapeHtml(item.wcCode)}</td>
      <td class="text-right">${item.employeeCount}</td>
      <td class="text-right">${fmt(item.grossWages)}</td>
      <td class="text-right current">${fmt(currentAdminDisplay)}</td>
      <td class="text-right current">${fmt(c.sutaTax)}</td>
      <td class="text-right current">${isBundled ? '—' : fmt(c.wcPremium)}</td>
      <td class="text-right amplify">${fmt(proposedAdminPerItem)}</td>
      <td class="text-right amplify">${fmt(pc.sutaTax)}</td>
      <td class="text-right amplify">${fmt(pc.wcPremium)}</td>
      <td class="text-right savings ${getSavingsClass(adminSave)}">${formatSavings(adminSave, fmt)}</td>
      <td class="text-right savings ${getSavingsClass(sutaSave)}">${formatSavings(sutaSave, fmt)}</td>
      <td class="text-right savings ${getSavingsClass(wcSave)}">${isBundled ? '—' : formatSavings(wcSave, fmt)}</td>
    </tr>`;
  }).join('');

  const currentAdminTotal = getCurrentAdminTotal(isBundled, isAdp, adpUnbundled, currentTotals.wcPremium, currentAdminFee, totalServiceFees);
  const adminSavingsTotal = currentAdminTotal - proposedAdminFee;
  const sutaSavingsTotal = currentTotals.sutaTax - proposedTotals.sutaTax;
  const wcSavingsTotal = currentTotals.wcPremium - proposedTotals.wcPremium;
  
  // Build ADP footnotes - combined for bundled, simple for unbundled
  // Build footnotes for special state types
  let stateFootnotes = '';
  if (hasClientStates) {
    stateFootnotes += '<p><span class="state-badge client">C</span> <strong>= Client State:</strong> SUTA is a pass-through (client\'s own rate applies).</p>';
  }
  if (hasMonopolisticStates) {
    stateFootnotes += '<p><span class="state-badge mono">M</span> <strong>= Monopolistic State:</strong> Workers\' comp is state-managed (ND, OH, WA, WY).</p>';
  }
  
  let adpFootnotes = '';
  if (isAdp) {
    if (isBundled) {
      adpFootnotes = '<p>* ADP Service Fee includes bundled Workers\' Comp and Admin Fee. Charges are calculated as a percent of gross wages.</p>';
    } else {
      adpFootnotes = '<p>ADP TotalSource: Charges are calculated as a percent of gross wages.</p>';
    }
  }

  return `<!DOCTYPE html>
<html>
<head>
  <title>Cost Comparison Proposal - ${escapeHtml(clientName) || 'Client'}</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body {
      height: 100%;
    }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #1a1a2e; 
      font-size: 11px; 
      line-height: 1.5; 
      background: white;
      -webkit-font-smoothing: antialiased;
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }
    .page-content {
      flex: 1;
    }
    
    /* State Badges */
    .state-badge {
      display: inline-block;
      font-size: 8px;
      font-weight: 700;
      padding: 1px 4px;
      border-radius: 3px;
      margin-left: 3px;
      vertical-align: middle;
    }
    .state-badge.client {
      background: #fef3c7;
      color: #d97706;
    }
    .state-badge.mono {
      background: #f3e8ff;
      color: #9333ea;
    }
    
    /* Header */
    .header { 
      display: flex; 
      justify-content: space-between; 
      align-items: flex-start; 
      padding-bottom: 16px; 
      border-bottom: 2px solid #3c68ff; 
      margin-bottom: 20px; 
    }
    .logo-section { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
    }
    .logo-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .company-name { 
      font-size: 24px; 
      font-weight: 700; 
      color: #3c68ff;
      letter-spacing: -0.5px;
    }
    .document-title { 
      font-size: 12px; 
      color: #64748b; 
      margin-top: 2px; 
      font-weight: 500; 
    }
    .client-info { text-align: right; }
    .client-name { 
      font-size: 16px; 
      font-weight: 600; 
      color: #1a1a2e; 
    }
    .client-states { 
      font-size: 11px; 
      color: #64748b; 
      margin-top: 2px; 
    }
    .meta-info { 
      font-size: 10px; 
      color: #64748b; 
      margin-top: 4px; 
      line-height: 1.5; 
    }
    
    /* Savings Banner */
    .savings-banner { 
      background: #f0fdf4;
      border: 1px solid #bbf7d0; 
      border-radius: 8px; 
      padding: 16px 24px; 
      text-align: center; 
      margin-bottom: 20px; 
    }
    .savings-label { 
      font-size: 10px; 
      font-weight: 600; 
      color: #166534; 
      text-transform: uppercase; 
      letter-spacing: 1px; 
    }
    .savings-amount { 
      font-size: 32px; 
      font-weight: 700; 
      color: #15803d; 
      margin: 4px 0; 
    }
    .savings-percent { 
      font-size: 12px; 
      font-weight: 500; 
      color: #166534; 
    }
    
    /* Summary Cards */
    .summary-grid { 
      display: flex; 
      gap: 16px; 
      margin-bottom: 20px; 
    }
    .summary-card { 
      flex: 1; 
      border-radius: 8px; 
      padding: 14px 16px; 
    }
    .summary-card.current { 
      background: #fef2f2; 
      border: 1px solid #fecaca; 
    }
    .summary-card.amplify { 
      background: #eff6ff; 
      border: 1px solid #bfdbfe; 
    }
    .card-title { 
      font-size: 10px; 
      font-weight: 700; 
      text-transform: uppercase; 
      letter-spacing: 0.5px; 
      margin-bottom: 10px; 
    }
    .card-title.current { color: #b91c1c; }
    .card-title.amplify { color: #1d4ed8; }
    .card-row { 
      display: flex; 
      justify-content: space-between; 
      padding: 3px 0; 
      font-size: 11px; 
    }
    .card-row .label { color: #64748b; }
    .card-row .value { font-weight: 600; color: #1a1a2e; }
    .card-total { 
      border-top: 1px solid #e2e8f0; 
      margin-top: 8px; 
      padding-top: 8px; 
      font-weight: 700; 
      font-size: 13px; 
    }
    .card-total.current { color: #b91c1c; }
    .card-total.amplify { color: #1d4ed8; }
    
    /* Table */
    .section-title { 
      font-size: 12px; 
      font-weight: 700; 
      color: #1a1a2e; 
      margin-bottom: 10px; 
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      font-size: 9px; 
    }
    th, td { 
      padding: 5px 6px; 
      border: 0.5px solid #cbd5e1; 
    }
    th { 
      font-weight: 600; 
      text-transform: uppercase; 
      font-size: 8px; 
      letter-spacing: 0.3px;
    }
    th.group-header { 
      text-align: center; 
      font-size: 9px; 
    }
    th.group-header.current { background: #fef2f2; color: #b91c1c; }
    th.group-header.amplify { background: #eff6ff; color: #1d4ed8; }
    th.group-header.savings { background: #f0fdf4; color: #166534; }
    th.sub-header { background: #f8fafc; color: #475569; }
    th.sub-header.current { background: #fef2f2; color: #b91c1c; }
    th.sub-header.amplify { background: #eff6ff; color: #1d4ed8; }
    th.sub-header.savings { background: #f0fdf4; color: #166534; }
    td.current { background: #fffbfb; }
    td.amplify { background: #f8faff; }
    td.savings { background: #f8fdfb; }
    td.positive { color: #15803d; font-weight: 600; }
    td.negative { color: #b91c1c; font-weight: 600; }
    .text-right { text-align: right; }
    tr.totals-row { font-weight: 600; background: #f8fafc; }
    tr.totals-row td.current { background: #fef2f2; }
    tr.totals-row td.amplify { background: #eff6ff; }
    tr.totals-row td.savings { background: #f0fdf4; }
    
    /* Footnote */
    .footnote { 
      background: #fffbeb; 
      border: 1px solid #fde68a; 
      border-radius: 6px; 
      padding: 10px 12px; 
      margin-top: 14px; 
      font-size: 9px; 
      color: #92400e;
      line-height: 1.6;
    }
    .footnote p { margin-bottom: 2px; }
    .footnote p:last-child { margin-bottom: 0; }
    
    /* Footer Container - Anchored to bottom */
    .footer-container {
      margin-top: auto;
      padding-top: 20px;
    }
    
    /* Footer Credentials Section */
    .footer-credentials { 
      padding: 16px 0 12px 0;
      border-top: 3px solid #3c68ff;
    }
    .credentials-row { 
      display: flex; 
      justify-content: center; 
      align-items: stretch; 
    }
    .credential-item { 
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 0 24px;
    }
    .credential-name {
      font-size: 13px;
      font-weight: 700;
      color: #1a1a2e;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .credential-desc {
      font-size: 9px;
      font-weight: 500;
      color: #64748b;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    .credential-separator {
      width: 1px;
      background-color: #d1d5db;
      align-self: stretch;
    }
    
    /* Footer */
    .footer { 
      margin-top: 12px; 
      padding: 10px 0; 
      font-size: 11px; 
      color: #64748b; 
      display: flex; 
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #e2e8f0;
      font-family: Arial, Helvetica, sans-serif;
    }
    .footer-left {
      font-weight: 600;
    }
    .footer-right {
      font-weight: 600;
    }
    .footer-center {
      font-weight: 600;
      font-style: italic;
      color: #dc2626;
      font-size: 10px;
    }
      font-weight: 500;
      font-style: italic;
      color: #b91c1c;
      font-size: 9px;
    }
  </style>
</head>
<body>
  <div class="page-content">
  <div class="header">
    <div class="logo-section">
      <div class="logo-icon">${LOGO_SVG}</div>
      <div>
        <div class="company-name">Amplify HR</div>
        <div class="document-title">Cost Comparison Proposal</div>
      </div>
    </div>
    <div class="client-info">
      <div class="client-name">${escapeHtml(clientName) || 'Client'}</div>
      ${statesList ? `<div class="client-states">${escapeHtml(statesList)}</div>` : ''}
      <div class="meta-info">
        ${preparedBy ? `Prepared by: ${escapeHtml(preparedBy)}<br>` : ''}
        Date: ${escapeHtml(quoteDate)}
      </div>
    </div>
  </div>

  ${savings > 0 ? `
  <div class="savings-banner">
    <div class="savings-label">Projected Annual Savings</div>
    <div class="savings-amount">${fmt(savings)}</div>
    <div class="savings-percent">${savingsPct}% reduction in total costs</div>
  </div>
  ` : ''}

  <div class="summary-grid">
    <div class="summary-card current">
      <div class="card-title current">${escapeHtml(currentProvider) || 'Current Provider'}</div>
      <div class="card-row"><span class="label">Gross Wages</span><span class="value">${fmt(currentTotals.grossWages)}</span></div>
      <div class="card-row"><span class="label">Employee Count</span><span class="value">${currentTotals.employees}</span></div>
      ${isBundled ? `
        <div class="card-row"><span class="label">ADP Service Fee *</span><span class="value">${fmt(currentTotals.wcPremium + currentAdminFee)}</span></div>
        <div class="card-row"><span class="label">SUTA Tax</span><span class="value">${fmt(currentTotals.sutaTax)}</span></div>
      ` : (isAdp && adpUnbundled ? `
        <div class="card-row"><span class="label">Workers' Comp</span><span class="value">${fmt(currentTotals.wcPremium)}</span></div>
        <div class="card-row"><span class="label">Service Fee</span><span class="value">${fmt(totalServiceFees)}</span></div>
        <div class="card-row"><span class="label">SUTA Tax</span><span class="value">${fmt(currentTotals.sutaTax)}</span></div>
      ` : `
        <div class="card-row"><span class="label">Admin Fee</span><span class="value">${fmt(currentAdminFee)}</span></div>
        <div class="card-row"><span class="label">SUTA Tax</span><span class="value">${fmt(currentTotals.sutaTax)}</span></div>
        <div class="card-row"><span class="label">Workers' Comp</span><span class="value">${fmt(currentTotals.wcPremium)}</span></div>
      `)}
      <div class="card-row card-total current"><span>Total</span><span class="value">${fmt(currentTotal)}</span></div>
    </div>
    <div class="summary-card amplify">
      <div class="card-title amplify">Amplify HR</div>
      <div class="card-row"><span class="label">Gross Wages</span><span class="value">${fmt(proposedTotals.grossWages)}</span></div>
      <div class="card-row"><span class="label">Employee Count</span><span class="value">${proposedTotals.employees}</span></div>
      <div class="card-row"><span class="label">Admin Fee</span><span class="value">${fmt(proposedAdminFee)}</span></div>
      <div class="card-row"><span class="label">SUTA Tax</span><span class="value">${fmt(proposedTotals.sutaTax)}</span></div>
      <div class="card-row"><span class="label">Workers' Comp</span><span class="value">${fmt(proposedTotals.wcPremium)}</span></div>
      <div class="card-row card-total amplify"><span>Total</span><span class="value">${fmt(proposedTotal)}</span></div>
    </div>
  </div>

  <div class="section-title">Line-by-Line Comparison</div>
  <table>
    <thead>
      <tr>
        <th rowspan="2" style="background:#f8fafc;">State</th>
        <th rowspan="2" style="background:#f8fafc;">Code</th>
        <th rowspan="2" class="text-right" style="background:#f8fafc;">EEs</th>
        <th rowspan="2" class="text-right" style="background:#f8fafc;">Wages</th>
        <th colspan="3" class="group-header current">${escapeHtml(currentProvider) || 'Current'}</th>
        <th colspan="3" class="group-header amplify">Amplify HR</th>
        <th colspan="3" class="group-header savings">Savings</th>
      </tr>
      <tr>
        <th class="text-right sub-header current">${isBundled ? 'Svc Fee *' : (isAdp ? 'Svc Fee' : 'Admin')}</th>
        <th class="text-right sub-header current">SUTA</th>
        <th class="text-right sub-header current">WC</th>
        <th class="text-right sub-header amplify">Admin</th>
        <th class="text-right sub-header amplify">SUTA</th>
        <th class="text-right sub-header amplify">WC</th>
        <th class="text-right sub-header savings">${isBundled ? 'Svc Fee *' : (isAdp ? 'Svc Fee' : 'Admin')}</th>
        <th class="text-right sub-header savings">SUTA</th>
        <th class="text-right sub-header savings">WC</th>
      </tr>
    </thead>
    <tbody>
      ${lineItemRows}
      <tr class="totals-row">
        <td colspan="2"><strong>Totals</strong></td>
        <td class="text-right">${currentTotals.employees}</td>
        <td class="text-right">${fmt(currentTotals.grossWages)}</td>
        <td class="text-right current">${fmt(currentAdminTotal)}</td>
        <td class="text-right current">${fmt(currentTotals.sutaTax)}</td>
        <td class="text-right current">${isBundled ? '—' : fmt(currentTotals.wcPremium)}</td>
        <td class="text-right amplify">${fmt(proposedAdminFee)}</td>
        <td class="text-right amplify">${fmt(proposedTotals.sutaTax)}</td>
        <td class="text-right amplify">${fmt(proposedTotals.wcPremium)}</td>
        <td class="text-right savings ${adminSavingsTotal > 0 ? 'positive' : 'negative'}">${fmt(adminSavingsTotal)}</td>
        <td class="text-right savings ${sutaSavingsTotal > 0 ? 'positive' : sutaSavingsTotal < 0 ? 'negative' : ''}">${fmt(sutaSavingsTotal)}</td>
        <td class="text-right savings ${wcSavingsTotal > 0 ? 'positive' : wcSavingsTotal < 0 ? 'negative' : ''}">${isBundled ? '—' : fmt(wcSavingsTotal)}</td>
      </tr>
    </tbody>
  </table>

  ${(isAdp || stateFootnotes) ? `<div class="footnote">${adpFootnotes}${stateFootnotes}</div>` : ''}
  </div>

  <div class="footer-container">
    <div class="footer-credentials">
      <div class="credentials-row">
        <div class="credential-item">
          <div class="credential-name">IRS CPEO</div>
          <div class="credential-desc">Certified</div>
        </div>
        <div class="credential-separator"></div>
        <div class="credential-item">
          <div class="credential-name">ESAC</div>
          <div class="credential-desc">Accredited</div>
        </div>
        <div class="credential-separator"></div>
        <div class="credential-item">
          <div class="credential-name">Inc. 5000 (4x)</div>
          <div class="credential-desc">Honoree</div>
        </div>
        <div class="credential-separator"></div>
        <div class="credential-item">
          <div class="credential-name">G2</div>
          <div class="credential-desc">High Performer</div>
        </div>
        <div class="credential-separator"></div>
        <div class="credential-item">
          <div class="credential-name">Forbes</div>
          <div class="credential-desc">Recognized</div>
        </div>
      </div>
    </div>

    <div class="footer">
      <span class="footer-left">amplifyhrm.com</span>
      <span class="footer-center">Confidential - Not for Distribution</span>
      <span class="footer-right">Amplify HR Pricing Engine</span>
    </div>
  </div>
</body>
</html>`;
}

export function useExportWorker() {
  const workerRef = useRef(null);
  const pendingRef = useRef(new Map());
  const idCounterRef = useRef(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  // Initialize worker
  useEffect(() => {
    if (isWorkerSupported) {
      try {
        // Create worker from blob for better compatibility
        const workerCode = `
          const fmt = (n) =>
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(Math.round(n));

          const calculate = (item, sutaBases) => {
            const wageCap = sutaBases[item.state] || 0;
            const ee = item.employeeCount || 1;
            const avgWage = item.grossWages / ee;
            const taxablePerEE = Math.min(avgWage, wageCap);
            const totalTaxableWages = taxablePerEE * ee;
            const sutaTax = totalTaxableWages * (item.sutaRate / 100);
            const wcPremium = (item.grossWages / 100) * item.wcRate;
            return { wageCap, avgWage, totalTaxableWages, sutaTax, wcPremium };
          };

          const LOGO_SVG = '<svg width="44" height="40" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 216 200"><path fill="#3c68ff" d="M 216 186.68 Q 216 186.83 216 186.99 L 199.96 200 L 198.33 200 L 96.05 2.58 A 0.23 0.23 0 0 1 96.25 2.25 L 119.68 2.25 A 0.93 0.92 -13.8 0 1 120.5 2.74 L 216 186.68 Z"/><path fill="#3c68ff" d="M 17.97 200 L 16.09 200 L 0.35 187.26 A 0.48 0.47 32.7 0 1 0.22 186.68 L 92.99 7.65 A 0.4 0.4 0 0 1 93.7 7.65 L 105.41 30.26 A 0.71 0.69 45.8 0 1 105.41 30.91 L 17.97 200 Z"/><path fill="#3c68ff" d="M 108 132.96 Q 108.23 132.96 108.26 132.98 Q 148.87 151.44 173.81 162.84 A 1.2 1.2 0 0 1 174.37 163.37 L 192.01 197.43 A 0.29 0.28 -1.8 0 1 191.64 197.82 Q 157.51 182.34 108.23 159.83 Q 108.22 159.82 108 159.82 Q 107.78 159.82 107.77 159.83 Q 58.49 182.35 24.37 197.83 A 0.29 0.28 1.8 0 1 24 197.44 L 41.63 163.38 A 1.2 1.2 0 0 1 42.19 162.85 Q 67.13 151.45 107.74 132.98 Q 107.77 132.96 108 132.96 Z"/></svg>';

          self.onmessage = (e) => {
            const { type, data, id } = e.data;
            try {
              if (type === 'GENERATE_PDF_HTML') {
                const html = generatePdfHtml(data);
                self.postMessage({ type: 'PDF_HTML_READY', id, result: html });
              }
            } catch (error) {
              self.postMessage({ type: 'ERROR', id, error: error.message });
            }
          };

          function generatePdfHtml(data) {
            const {
              lineItems, proposedItems, sutaBases, currentTotals, proposedTotals,
              currentAdminFee, proposedAdminFee, currentTotal, proposedTotal,
              savings, savingsPct, isAdp, adpUnbundled, serviceFees, totalServiceFees,
              clientName, currentProvider, preparedBy, quoteDate,
            } = data;

            const statesList = [...new Set(lineItems.map(i => i.state))].sort().join(", ");
            const isBundled = isAdp && !adpUnbundled;
            
            const lineItemRows = lineItems.map(item => {
              const c = calculate(item, sutaBases);
              const p = proposedItems.find(pi => pi.id === item.id);
              const pc = calculate(p, sutaBases);
              const currentAdminPerItem = currentTotals.employees > 0 ? (currentAdminFee * item.employeeCount / currentTotals.employees) : 0;
              const proposedAdminPerItem = proposedTotals.employees > 0 ? (proposedAdminFee * item.employeeCount / proposedTotals.employees) : 0;
              const svcFeeRate = serviceFees && serviceFees[item.id] ? serviceFees[item.id].rate || 0 : 0;
              const svcFeeAmt = (item.grossWages / 100) * svcFeeRate;
              const currentAdminDisplay = isBundled ? c.wcPremium + currentAdminPerItem : (isAdp && adpUnbundled ? svcFeeAmt : currentAdminPerItem);
              const wcSave = c.wcPremium - pc.wcPremium;
              const sutaSave = c.sutaTax - pc.sutaTax;
              const adminSave = isBundled ? (c.wcPremium + currentAdminPerItem) - proposedAdminPerItem : (isAdp && adpUnbundled ? svcFeeAmt - proposedAdminPerItem : currentAdminPerItem - proposedAdminPerItem);
              
              return '<tr><td>' + item.state + '</td><td>' + item.wcCode + '</td><td class="text-right">' + item.employeeCount + '</td><td class="text-right">' + fmt(item.grossWages) + '</td><td class="text-right current">' + fmt(currentAdminDisplay) + '</td><td class="text-right current">' + fmt(c.sutaTax) + '</td><td class="text-right current">' + (isBundled ? '—' : fmt(c.wcPremium)) + '</td><td class="text-right amplify">' + fmt(proposedAdminPerItem) + '</td><td class="text-right amplify">' + fmt(pc.sutaTax) + '</td><td class="text-right amplify">' + fmt(pc.wcPremium) + '</td><td class="text-right savings ' + (adminSave > 0 ? 'positive' : adminSave < 0 ? 'negative' : '') + '">' + (adminSave !== 0 ? fmt(adminSave) : '—') + '</td><td class="text-right savings ' + (sutaSave > 0 ? 'positive' : sutaSave < 0 ? 'negative' : '') + '">' + (sutaSave !== 0 ? fmt(sutaSave) : '—') + '</td><td class="text-right savings ' + (wcSave > 0 ? 'positive' : wcSave < 0 ? 'negative' : '') + '">' + (isBundled ? '—' : (wcSave !== 0 ? fmt(wcSave) : '—')) + '</td></tr>';
            }).join('');

            const currentAdminTotal = isBundled ? currentTotals.wcPremium + currentAdminFee : (isAdp && adpUnbundled ? totalServiceFees : currentAdminFee);
            const adminSavingsTotal = currentAdminTotal - proposedAdminFee;
            const sutaSavingsTotal = currentTotals.sutaTax - proposedTotals.sutaTax;
            const wcSavingsTotal = currentTotals.wcPremium - proposedTotals.wcPremium;
            
            let adpFootnotes = '';
            if (isAdp) {
              if (isBundled) {
                adpFootnotes = "<p>* ADP Service Fee includes bundled Workers' Comp and Admin Fee. Charges are calculated as a percent of gross wages.</p>";
              } else {
                adpFootnotes = '<p>ADP TotalSource: Charges are calculated as a percent of gross wages.</p>';
              }
            }

            return '<!DOCTYPE html><html><head><title>Cost Comparison Proposal - ' + (clientName || 'Client') + '</title><style>@page { size: letter portrait; margin: 0.5in; }* { box-sizing: border-box; margin: 0; padding: 0; }html, body { height: 100%; }body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color: #1a1a2e; font-size: 11px; line-height: 1.5; background: white; -webkit-font-smoothing: antialiased; display: flex; flex-direction: column; min-height: 100%; }.page-content { flex: 1; }.header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 16px; border-bottom: 2px solid #3c68ff; margin-bottom: 20px; }.logo-section { display: flex; align-items: center; gap: 12px; }.logo-icon { display: flex; align-items: center; justify-content: center; }.company-name { font-size: 24px; font-weight: 700; color: #3c68ff; letter-spacing: -0.5px; }.document-title { font-size: 12px; color: #64748b; margin-top: 2px; font-weight: 500; }.client-info { text-align: right; }.client-name { font-size: 16px; font-weight: 600; color: #1a1a2e; }.client-states { font-size: 11px; color: #64748b; margin-top: 2px; }.meta-info { font-size: 10px; color: #64748b; margin-top: 4px; line-height: 1.5; }.savings-banner { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px 24px; text-align: center; margin-bottom: 20px; }.savings-label { font-size: 10px; font-weight: 600; color: #166534; text-transform: uppercase; letter-spacing: 1px; }.savings-amount { font-size: 32px; font-weight: 700; color: #15803d; margin: 4px 0; }.savings-percent { font-size: 12px; font-weight: 500; color: #166534; }.summary-grid { display: flex; gap: 16px; margin-bottom: 20px; }.summary-card { flex: 1; border-radius: 8px; padding: 14px 16px; }.summary-card.current { background: #fef2f2; border: 1px solid #fecaca; }.summary-card.amplify { background: #eff6ff; border: 1px solid #bfdbfe; }.card-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }.card-title.current { color: #b91c1c; }.card-title.amplify { color: #1d4ed8; }.card-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 11px; }.card-row .label { color: #64748b; }.card-row .value { font-weight: 600; color: #1a1a2e; }.card-total { border-top: 1px solid #e2e8f0; margin-top: 8px; padding-top: 8px; font-weight: 700; font-size: 13px; }.card-total.current { color: #b91c1c; }.card-total.amplify { color: #1d4ed8; }.section-title { font-size: 12px; font-weight: 700; color: #1a1a2e; margin-bottom: 10px; }table { width: 100%; border-collapse: collapse; font-size: 9px; }th, td { padding: 5px 6px; border: 0.5px solid #cbd5e1; }th { font-weight: 600; text-transform: uppercase; font-size: 8px; letter-spacing: 0.3px; }th.group-header { text-align: center; font-size: 9px; }th.group-header.current { background: #fef2f2; color: #b91c1c; }th.group-header.amplify { background: #eff6ff; color: #1d4ed8; }th.group-header.savings { background: #f0fdf4; color: #166534; }th.sub-header { background: #f8fafc; color: #475569; }th.sub-header.current { background: #fef2f2; color: #b91c1c; }th.sub-header.amplify { background: #eff6ff; color: #1d4ed8; }th.sub-header.savings { background: #f0fdf4; color: #166534; }td.current { background: #fffbfb; }td.amplify { background: #f8faff; }td.savings { background: #f8fdfb; }td.positive { color: #15803d; font-weight: 600; }td.negative { color: #b91c1c; font-weight: 600; }.text-right { text-align: right; }tr.totals-row { font-weight: 600; background: #f8fafc; }tr.totals-row td.current { background: #fef2f2; }tr.totals-row td.amplify { background: #eff6ff; }tr.totals-row td.savings { background: #f0fdf4; }.footnote { background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 10px 12px; margin-top: 14px; font-size: 9px; color: #92400e; line-height: 1.6; }.footnote p { margin-bottom: 2px; }.footnote p:last-child { margin-bottom: 0; }.footer-container { margin-top: auto; padding-top: 20px; }.footer-credentials { padding: 16px 0 12px 0; border-top: 3px solid #3c68ff; }.credentials-row { display: flex; justify-content: center; align-items: stretch; }.credential-item { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 0 24px; }.credential-name { font-size: 13px; font-weight: 700; color: #1a1a2e; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 4px; }.credential-desc { font-size: 9px; font-weight: 500; color: #64748b; letter-spacing: 1px; text-transform: uppercase; }.credential-separator { width: 1px; background-color: #d1d5db; align-self: stretch; }.footer { margin-top: 12px; padding: 10px 0; font-size: 11px; color: #64748b; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #e2e8f0; font-family: Arial, Helvetica, sans-serif; }.footer-left { font-weight: 600; }.footer-right { font-weight: 600; }.footer-center { font-weight: 600; font-style: italic; color: #dc2626; font-size: 10px; }</style></head><body><div class="page-content"><div class="header"><div class="logo-section"><div class="logo-icon">' + LOGO_SVG + '</div><div><div class="company-name">Amplify HR</div><div class="document-title">Cost Comparison Proposal</div></div></div><div class="client-info"><div class="client-name">' + (clientName || 'Client') + '</div>' + (statesList ? '<div class="client-states">' + statesList + '</div>' : '') + '<div class="meta-info">' + (preparedBy ? 'Prepared by: ' + preparedBy + '<br>' : '') + 'Date: ' + quoteDate + '</div></div></div>' + (savings > 0 ? '<div class="savings-banner"><div class="savings-label">Projected Annual Savings</div><div class="savings-amount">' + fmt(savings) + '</div><div class="savings-percent">' + savingsPct + '% reduction in total costs</div></div>' : '') + '<div class="summary-grid"><div class="summary-card current"><div class="card-title current">' + (currentProvider || 'Current Provider') + '</div><div class="card-row"><span class="label">Gross Wages</span><span class="value">' + fmt(currentTotals.grossWages) + '</span></div><div class="card-row"><span class="label">Employee Count</span><span class="value">' + currentTotals.employees + '</span></div>' + (isBundled ? '<div class="card-row"><span class="label">ADP Service Fee *</span><span class="value">' + fmt(currentTotals.wcPremium + currentAdminFee) + '</span></div><div class="card-row"><span class="label">SUTA Tax</span><span class="value">' + fmt(currentTotals.sutaTax) + '</span></div>' : (isAdp && adpUnbundled ? '<div class="card-row"><span class="label">Workers\\' Comp</span><span class="value">' + fmt(currentTotals.wcPremium) + '</span></div><div class="card-row"><span class="label">Service Fee</span><span class="value">' + fmt(totalServiceFees) + '</span></div><div class="card-row"><span class="label">SUTA Tax</span><span class="value">' + fmt(currentTotals.sutaTax) + '</span></div>' : '<div class="card-row"><span class="label">Admin Fee</span><span class="value">' + fmt(currentAdminFee) + '</span></div><div class="card-row"><span class="label">SUTA Tax</span><span class="value">' + fmt(currentTotals.sutaTax) + '</span></div><div class="card-row"><span class="label">Workers\\' Comp</span><span class="value">' + fmt(currentTotals.wcPremium) + '</span></div>')) + '<div class="card-row card-total current"><span>Total</span><span class="value">' + fmt(currentTotal) + '</span></div></div><div class="summary-card amplify"><div class="card-title amplify">Amplify HR</div><div class="card-row"><span class="label">Gross Wages</span><span class="value">' + fmt(proposedTotals.grossWages) + '</span></div><div class="card-row"><span class="label">Employee Count</span><span class="value">' + proposedTotals.employees + '</span></div><div class="card-row"><span class="label">Admin Fee</span><span class="value">' + fmt(proposedAdminFee) + '</span></div><div class="card-row"><span class="label">SUTA Tax</span><span class="value">' + fmt(proposedTotals.sutaTax) + '</span></div><div class="card-row"><span class="label">Workers\\' Comp</span><span class="value">' + fmt(proposedTotals.wcPremium) + '</span></div><div class="card-row card-total amplify"><span>Total</span><span class="value">' + fmt(proposedTotal) + '</span></div></div></div><div class="section-title">Line-by-Line Comparison</div><table><thead><tr><th rowspan="2" style="background:#f8fafc;">State</th><th rowspan="2" style="background:#f8fafc;">Code</th><th rowspan="2" class="text-right" style="background:#f8fafc;">EEs</th><th rowspan="2" class="text-right" style="background:#f8fafc;">Wages</th><th colspan="3" class="group-header current">' + (currentProvider || 'Current') + '</th><th colspan="3" class="group-header amplify">Amplify HR</th><th colspan="3" class="group-header savings">Savings</th></tr><tr><th class="text-right sub-header current">' + (isBundled ? 'Svc Fee *' : (isAdp ? 'Svc Fee' : 'Admin')) + '</th><th class="text-right sub-header current">SUTA</th><th class="text-right sub-header current">WC</th><th class="text-right sub-header amplify">Admin</th><th class="text-right sub-header amplify">SUTA</th><th class="text-right sub-header amplify">WC</th><th class="text-right sub-header savings">' + (isBundled ? 'Svc Fee *' : (isAdp ? 'Svc Fee' : 'Admin')) + '</th><th class="text-right sub-header savings">SUTA</th><th class="text-right sub-header savings">WC</th></tr></thead><tbody>' + lineItemRows + '<tr class="totals-row"><td colspan="2"><strong>Totals</strong></td><td class="text-right">' + currentTotals.employees + '</td><td class="text-right">' + fmt(currentTotals.grossWages) + '</td><td class="text-right current">' + fmt(currentAdminTotal) + '</td><td class="text-right current">' + fmt(currentTotals.sutaTax) + '</td><td class="text-right current">' + (isBundled ? '—' : fmt(currentTotals.wcPremium)) + '</td><td class="text-right amplify">' + fmt(proposedAdminFee) + '</td><td class="text-right amplify">' + fmt(proposedTotals.sutaTax) + '</td><td class="text-right amplify">' + fmt(proposedTotals.wcPremium) + '</td><td class="text-right savings ' + (adminSavingsTotal > 0 ? 'positive' : 'negative') + '">' + fmt(adminSavingsTotal) + '</td><td class="text-right savings ' + (sutaSavingsTotal > 0 ? 'positive' : sutaSavingsTotal < 0 ? 'negative' : '') + '">' + fmt(sutaSavingsTotal) + '</td><td class="text-right savings ' + (wcSavingsTotal > 0 ? 'positive' : wcSavingsTotal < 0 ? 'negative' : '') + '">' + (isBundled ? '—' : fmt(wcSavingsTotal)) + '</td></tr></tbody></table>' + (isAdp ? '<div class="footnote">' + adpFootnotes + '</div>' : '') + '</div><div class="footer-container"><div class="footer-credentials"><div class="credentials-row"><div class="credential-item"><div class="credential-name">IRS CPEO</div><div class="credential-desc">Certified</div></div><div class="credential-separator"></div><div class="credential-item"><div class="credential-name">ESAC</div><div class="credential-desc">Accredited</div></div><div class="credential-separator"></div><div class="credential-item"><div class="credential-name">Inc. 5000 (4x)</div><div class="credential-desc">Honoree</div></div><div class="credential-separator"></div><div class="credential-item"><div class="credential-name">G2</div><div class="credential-desc">High Performer</div></div><div class="credential-separator"></div><div class="credential-item"><div class="credential-name">Forbes</div><div class="credential-desc">Recognized</div></div></div></div><div class="footer"><span class="footer-left">amplifyhrm.com</span><span class="footer-center">Confidential - Not for Distribution</span><span class="footer-right">Amplify HR Pricing Engine</span></div></div></body></html>';
          }
        `;
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        workerRef.current = new Worker(URL.createObjectURL(blob));
        
        workerRef.current.onmessage = (e) => {
          const { type, id, result, error } = e.data;
          const pending = pendingRef.current.get(id);
          if (pending) {
            if (type === 'ERROR') {
              pending.reject(new Error(error));
            } else {
              pending.resolve(result);
            }
            pendingRef.current.delete(id);
          }
        };
      } catch {
        console.warn('Web Worker initialization failed, using main thread fallback');
      }
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  // Generate Excel using main thread (xlsx library doesn't work well in workers)
  const generateExcel = useCallback(async (data) => {
    setIsExporting(true);
    setExportProgress('Generating Excel...');
    
    try {
      const {
        lineItems, proposedItems, sutaBases, sutaRates, WC_DESCRIPTIONS, MONOPOLISTIC_STATES,
        currentTotals, proposedTotals, currentAdminFee, proposedAdminFee,
        currentTotal, proposedTotal, savings, savingsPct, isAdp: _isAdp,
        clientName, currentProvider, preparedBy, quoteDate,
      } = data;

      const wb = XLSX.utils.book_new();
      
      // Summary sheet (first sheet - vertical layout with Current vs Amplify columns)
      const summaryData = [
        ["", currentProvider || "Current", "Amplify HR", "Savings"],
        ["Gross Wages", currentTotals.grossWages, proposedTotals.grossWages, ""],
        ["Employee Count", currentTotals.employees, proposedTotals.employees, ""],
        ["Admin Fee", Math.round(currentAdminFee), Math.round(proposedAdminFee), Math.round(currentAdminFee - proposedAdminFee)],
        ["SUTA Tax", Math.round(currentTotals.sutaTax), Math.round(proposedTotals.sutaTax), Math.round(currentTotals.sutaTax - proposedTotals.sutaTax)],
        ["Workers' Comp", Math.round(currentTotals.wcPremium), Math.round(proposedTotals.wcPremium), Math.round(currentTotals.wcPremium - proposedTotals.wcPremium)],
        ["", "", "", ""],
        ["TOTAL", Math.round(currentTotal), Math.round(proposedTotal), Math.round(savings)],
        ["", "", "", ""],
        ["Savings %", "", "", savingsPct + "%"],
      ];
      
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Set column widths
      wsSummary['!cols'] = [
        { wch: 15 }, // Metric name
        { wch: 15 }, // Current
        { wch: 15 }, // Amplify
        { wch: 12 }, // Savings
      ];
      
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      // Client Info sheet
      const clientInfoData = [
        ["Client Information"],
        [""],
        ["Client Name", clientName || "N/A"],
        ["Current Provider", currentProvider || "N/A"],
        ["Prepared By", preparedBy || "N/A"],
        ["Quote Date", quoteDate],
      ];
      
      const wsClientInfo = XLSX.utils.aoa_to_sheet(clientInfoData);
      wsClientInfo['!cols'] = [{ wch: 18 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(wb, wsClientInfo, "Client Info");
      
      // Line Items detail sheet
      const detailRows = lineItems.map(item => {
        const c = calculate(item, sutaBases);
        const p = proposedItems.find(pi => pi.id === item.id);
        const pc = calculate(p, sutaBases);
        return {
          State: item.state,
          "WC Code": item.wcCode,
          Description: WC_DESCRIPTIONS[item.wcCode] || "",
          Employees: item.employeeCount,
          "Gross Wages": item.grossWages,
          "Current WC Rate": item.wcRate + "%",
          "Current WC Premium": Math.round(c.wcPremium),
          "Current SUTA Rate": item.sutaRate + "%",
          "Current SUTA Tax": Math.round(c.sutaTax),
          "Amplify WC Rate": (p?.wcRate || 0) + "%",
          "Amplify WC Premium": Math.round(pc.wcPremium),
          "Amplify SUTA Rate": (p?.sutaRate || 0) + "%",
          "Amplify SUTA Tax": Math.round(pc.sutaTax),
          "WC Savings": Math.round(c.wcPremium - pc.wcPremium),
          "SUTA Savings": Math.round(c.sutaTax - pc.sutaTax),
        };
      });

      const wsDetail = XLSX.utils.json_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(wb, wsDetail, "Line Items");

      // Amplify Workers Comp Report sheet - breakdown by State and Class Code
      const wcReportData = [
        ["AMPLIFY WORKERS COMP REPORT"],
        [],
        ["State", "Class Code", "Description", "Employee Count", "Gross Wages", "WC Rate", "SUTA Rate", "SUTA Wage Base", "SUTA Reporting Type", "WC Rating Type"],
      ];
      
      // Sort by state then by class code
      const sortedItems = [...lineItems].sort((a, b) => {
        if (a.state !== b.state) return a.state.localeCompare(b.state);
        return a.wcCode.localeCompare(b.wcCode);
      });
      
      let currentState = null;
      let stateTotal = { employees: 0, wages: 0 };
      
      sortedItems.forEach((item, idx) => {
        // Add state subtotal when state changes
        if (currentState && currentState !== item.state) {
          wcReportData.push([
            `${currentState} Total`,
            "",
            "",
            stateTotal.employees,
            stateTotal.wages,
            "",
            "",
            "",
            "",
            ""
          ]);
          wcReportData.push([]); // blank row between states
          stateTotal = { employees: 0, wages: 0 };
        }
        
        currentState = item.state;
        stateTotal.employees += item.employeeCount;
        stateTotal.wages += item.grossWages;
        
        // Get the proposed rates for this item
        const proposedItem = proposedItems.find(pi => pi.id === item.id);
        const wcRate = proposedItem?.wcRate || item.wcRate;
        const sutaRate = proposedItem?.sutaRate || item.sutaRate;
        const sutaWageBase = sutaBases[item.state] || 0;
        
        // Determine SUTA reporting type (Client Reporting if bill is null, PEO Reporting otherwise)
        const sutaEntry = sutaRates?.[item.state];
        const sutaType = (sutaEntry?.bill === null || sutaEntry?.bill === undefined) 
          ? "Client Reporting" 
          : "PEO Reporting";
        
        // Determine WC type (Monopolistic if in MONOPOLISTIC_STATES, PEO Rating otherwise)
        const wcType = (MONOPOLISTIC_STATES || []).includes(item.state) 
          ? "Monopolistic" 
          : "PEO Rating";
        
        wcReportData.push([
          item.state,
          item.wcCode,
          WC_DESCRIPTIONS[item.wcCode] || "",
          item.employeeCount,
          item.grossWages,
          wcRate + "%",
          sutaRate + "%",
          sutaWageBase,
          sutaType,
          wcType
        ]);
        
        // Add final state subtotal
        if (idx === sortedItems.length - 1) {
          wcReportData.push([
            `${currentState} Total`,
            "",
            "",
            stateTotal.employees,
            stateTotal.wages,
            "",
            "",
            "",
            "",
            ""
          ]);
        }
      });
      
      // Grand total
      wcReportData.push([]);
      wcReportData.push([
        "GRAND TOTAL",
        "",
        "",
        lineItems.reduce((sum, i) => sum + i.employeeCount, 0),
        lineItems.reduce((sum, i) => sum + i.grossWages, 0),
        "",
        "",
        "",
        "",
        ""
      ]);
      
      const wsWcReport = XLSX.utils.aoa_to_sheet(wcReportData);
      
      // Set column widths
      wsWcReport['!cols'] = [
        { wch: 15 },  // State
        { wch: 12 },  // Class Code
        { wch: 35 },  // Description
        { wch: 15 },  // Employee Count
        { wch: 18 },  // Gross Wages
        { wch: 12 },  // WC Rate
        { wch: 12 },  // SUTA Rate
        { wch: 15 },  // SUTA Wage Base
        { wch: 20 },  // SUTA Reporting Type
        { wch: 15 },  // WC Rating Type
      ];
      
      XLSX.utils.book_append_sheet(wb, wsWcReport, "WC Report");

      // Download
      XLSX.writeFile(wb, `${clientName || 'Quote'}_Amplify_HR_Proposal_${quoteDate}.xlsx`);
      setExportProgress('');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Generate PDF using worker for HTML generation, main thread for rendering
  const generatePdf = useCallback(async (data) => {
    setIsExporting(true);
    setExportProgress('Generating PDF...');
    
    try {
      let htmlContent;
      
      if (workerRef.current) {
        // Use worker for HTML generation
        const id = idCounterRef.current++;
        htmlContent = await new Promise((resolve, reject) => {
          pendingRef.current.set(id, { resolve, reject });
          workerRef.current.postMessage({ type: 'GENERATE_PDF_HTML', data, id });
          
          // Timeout after 30 seconds
          setTimeout(() => {
            if (pendingRef.current.has(id)) {
              pendingRef.current.delete(id);
              reject(new Error('PDF generation timed out'));
            }
          }, 30000);
        });
      } else {
        // Fallback: generate HTML on main thread
        htmlContent = generatePdfHtml(data);
      }
      
      setExportProgress('Rendering PDF...');
      
      // Render PDF on main thread (html2pdf requires DOM access)
      // Sanitize HTML to prevent XSS even though content is internally generated
      const container = document.createElement('div');
      container.innerHTML = DOMPurify.sanitize(htmlContent, { 
        ADD_TAGS: ['style'],
        ADD_ATTR: ['class', 'colspan', 'rowspan']
      });
      document.body.appendChild(container);
      
      const opt = {
        margin: [0.4, 0.4, 0.4, 0.4],
        filename: `${data.clientName || 'Quote'}_Amplify_HR_Proposal_${data.quoteDate}.pdf`,
        image: { type: 'png', quality: 1 },
        html2canvas: { 
          scale: 4,
          useCORS: true,
          letterRendering: true,
          logging: false,
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      
      await html2pdf().set(opt).from(container).save();
      document.body.removeChild(container);
      setExportProgress('');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Generate Word document - Professional Proposal Format
  const generateWord = useCallback(async (data) => {
    setIsExporting(true);
    setExportProgress('Generating Word document...');
    
    try {
      const {
        clientName = 'Client',
        currentProvider = 'Current Provider',
        preparedBy = '',
        quoteDate = new Date().toISOString().split('T')[0],
        lineItems = [],
        proposedItems = [],
        currentTotals = {},
        proposedTotals = {},
        currentAdminFee = 0,
        proposedAdminFee = 0,
        currentTotal: passedCurrentTotal,
        proposedTotal: passedProposedTotal,
        savings = 0,
        savingsPct = 0,
        sutaBases = {},
      } = data;

      // Use passed totals or calculate from components
      const currentTotal = passedCurrentTotal || ((currentTotals.wcPremium || 0) + (currentTotals.sutaTax || 0) + currentAdminFee);
      const proposedTotal = passedProposedTotal || ((proposedTotals.wcPremium || 0) + (proposedTotals.sutaTax || 0) + proposedAdminFee);

      // Define professional table borders
      const tableBorders = {
        top: { style: BorderStyle.SINGLE, size: 1, color: 'e2e8f0' },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: 'e2e8f0' },
        left: { style: BorderStyle.SINGLE, size: 1, color: 'e2e8f0' },
        right: { style: BorderStyle.SINGLE, size: 1, color: 'e2e8f0' },
      };

      // Helper to create professional table cell
      const createCell = (text, options = {}) => {
        return new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ 
              text: String(text), 
              bold: options.bold || false,
              size: options.size || 20,
              color: options.color || '374151',
              font: 'Calibri',
            })],
            alignment: options.alignment || AlignmentType.LEFT,
            spacing: { before: 60, after: 60 },
          })],
          width: options.width ? { size: options.width, type: WidthType.PERCENTAGE } : undefined,
          shading: options.shading ? { fill: options.shading, type: ShadingType.CLEAR } : undefined,
          borders: tableBorders,
          margins: { top: 50, bottom: 50, left: 100, right: 100 },
        });
      };

      // Create document sections
      const children = [];

      // Create a two-column header table: Logo/Company on left, Client info on right
      const statesList = [...new Set(lineItems.map(i => i.state))].sort().join(', ');
      
      // Build the left column content (company name only - no logo for Word compatibility)
      const leftColumnChildren = [];
      
      leftColumnChildren.push(
        new Paragraph({
          children: [new TextRun({ text: 'AMPLIFY HR', bold: true, size: 44, color: '000000', font: 'Calibri' })],
          spacing: { after: 50 },
        })
      );
      
      leftColumnChildren.push(
        new Paragraph({
          children: [new TextRun({ text: 'Cost Comparison Proposal', size: 28, color: '64748b', font: 'Calibri' })],
        })
      );
      
      const headerTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NIL },
          bottom: { style: BorderStyle.NIL },
          left: { style: BorderStyle.NIL },
          right: { style: BorderStyle.NIL },
          insideHorizontal: { style: BorderStyle.NIL },
          insideVertical: { style: BorderStyle.NIL },
        },
        rows: [
          new TableRow({
            children: [
              // Left column: Logo + Company name and title
              new TableCell({
                children: leftColumnChildren,
                width: { size: 50, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NIL },
                  bottom: { style: BorderStyle.NIL },
                  left: { style: BorderStyle.NIL },
                  right: { style: BorderStyle.NIL },
                },
              }),
              // Right column: Client info (aligned right)
              new TableCell({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: 'PREPARED FOR', bold: true, size: 18, color: '6b7280', font: 'Calibri' })],
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 30 },
                  }),
                  new Paragraph({
                    children: [new TextRun({ text: clientName, bold: true, size: 28, color: '111827', font: 'Calibri' })],
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 80 },
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Prepared by: ', size: 20, color: '6b7280', font: 'Calibri' }),
                      new TextRun({ text: preparedBy || 'Amplify HR', size: 20, color: '374151', font: 'Calibri' }),
                    ],
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 30 },
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Date: ', size: 20, color: '6b7280', font: 'Calibri' }),
                      new TextRun({ text: quoteDate, size: 20, color: '374151', font: 'Calibri' }),
                    ],
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 30 },
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'States: ', size: 20, color: '6b7280', font: 'Calibri' }),
                      new TextRun({ text: statesList, size: 20, color: '374151', font: 'Calibri' }),
                    ],
                    alignment: AlignmentType.RIGHT,
                  }),
                ],
                width: { size: 50, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.NIL },
                  bottom: { style: BorderStyle.NIL },
                  left: { style: BorderStyle.NIL },
                  right: { style: BorderStyle.NIL },
                },
              }),
            ],
          }),
        ],
      });
      
      children.push(headerTable);
      
      // Blue divider line under header
      children.push(
        new Paragraph({
          border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: '3c68ff' } },
          spacing: { before: 200, after: 300 },
        })
      );

      // Savings Banner (if there are savings)
      if (savings > 0) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'PROJECTED ANNUAL SAVINGS', bold: true, size: 20, color: '166534', font: 'Calibri' })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 200 },
          }),
          new Paragraph({
            children: [new TextRun({ text: fmt(savings), bold: true, size: 56, color: '15803d', font: 'Calibri' })],
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({
            children: [new TextRun({ text: `${savingsPct}% reduction in total costs`, size: 22, color: '166534', font: 'Calibri' })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          })
        );
      }

      // Executive Summary Section
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'EXECUTIVE SUMMARY', bold: true, size: 24, color: '1f2937', font: 'Calibri' })],
          spacing: { before: 300, after: 200 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'e5e7eb' } },
        })
      );

      // Summary Table - Professional styling
      const summaryTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: [
              createCell('', { width: 40, shading: 'f9fafb' }),
              createCell(currentProvider, { bold: true, alignment: AlignmentType.CENTER, shading: 'fef2f2', color: 'b91c1c' }),
              createCell('Amplify HR', { bold: true, alignment: AlignmentType.CENTER, shading: 'eff6ff', color: '1d4ed8' }),
              createCell('Savings', { bold: true, alignment: AlignmentType.CENTER, shading: 'f0fdf4', color: '166534' }),
            ],
          }),
          new TableRow({
            children: [
              createCell('Gross Wages', { shading: 'f9fafb' }),
              createCell(fmt(currentTotals.grossWages || 0), { alignment: AlignmentType.RIGHT }),
              createCell(fmt(proposedTotals.grossWages || 0), { alignment: AlignmentType.RIGHT }),
              createCell('—', { alignment: AlignmentType.CENTER, color: '9ca3af' }),
            ],
          }),
          new TableRow({
            children: [
              createCell('Employee Count', { shading: 'f9fafb' }),
              createCell(String(currentTotals.employees || 0), { alignment: AlignmentType.RIGHT }),
              createCell(String(proposedTotals.employees || 0), { alignment: AlignmentType.RIGHT }),
              createCell('—', { alignment: AlignmentType.CENTER, color: '9ca3af' }),
            ],
          }),
          new TableRow({
            children: [
              createCell('Admin Fee', { shading: 'f9fafb' }),
              createCell(fmt(currentAdminFee), { alignment: AlignmentType.RIGHT }),
              createCell(fmt(proposedAdminFee), { alignment: AlignmentType.RIGHT }),
              createCell(fmt(currentAdminFee - proposedAdminFee), { alignment: AlignmentType.RIGHT, color: (currentAdminFee - proposedAdminFee) > 0 ? '166534' : 'b91c1c' }),
            ],
          }),
          new TableRow({
            children: [
              createCell('SUTA Tax', { shading: 'f9fafb' }),
              createCell(fmt(currentTotals.sutaTax || 0), { alignment: AlignmentType.RIGHT }),
              createCell(fmt(proposedTotals.sutaTax || 0), { alignment: AlignmentType.RIGHT }),
              createCell(fmt((currentTotals.sutaTax || 0) - (proposedTotals.sutaTax || 0)), { alignment: AlignmentType.RIGHT, color: ((currentTotals.sutaTax || 0) - (proposedTotals.sutaTax || 0)) >= 0 ? '166534' : 'b91c1c' }),
            ],
          }),
          new TableRow({
            children: [
              createCell("Workers' Comp", { shading: 'f9fafb' }),
              createCell(fmt(currentTotals.wcPremium || 0), { alignment: AlignmentType.RIGHT }),
              createCell(fmt(proposedTotals.wcPremium || 0), { alignment: AlignmentType.RIGHT }),
              createCell(fmt((currentTotals.wcPremium || 0) - (proposedTotals.wcPremium || 0)), { alignment: AlignmentType.RIGHT, color: ((currentTotals.wcPremium || 0) - (proposedTotals.wcPremium || 0)) >= 0 ? '166534' : 'b91c1c' }),
            ],
          }),
          new TableRow({
            children: [
              createCell('TOTAL ANNUAL COST', { bold: true, shading: 'f3f4f6' }),
              createCell(fmt(currentTotal), { bold: true, alignment: AlignmentType.RIGHT, shading: 'fef2f2', color: 'b91c1c' }),
              createCell(fmt(proposedTotal), { bold: true, alignment: AlignmentType.RIGHT, shading: 'eff6ff', color: '1d4ed8' }),
              createCell(fmt(savings), { bold: true, alignment: AlignmentType.RIGHT, shading: 'f0fdf4', color: '166534' }),
            ],
          }),
        ],
      });
      children.push(summaryTable);

      // Line-by-Line Comparison Section
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'LINE-BY-LINE COMPARISON', bold: true, size: 24, color: '1f2937', font: 'Calibri' })],
          spacing: { before: 500, after: 200 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'e5e7eb' } },
        })
      );

      // Create header row
      const headerRow = new TableRow({
        children: [
          createCell('State', { bold: true, shading: 'f3f4f6', size: 18 }),
          createCell('Code', { bold: true, shading: 'f3f4f6', size: 18 }),
          createCell('EEs', { bold: true, shading: 'f3f4f6', alignment: AlignmentType.RIGHT, size: 18 }),
          createCell('Gross Wages', { bold: true, shading: 'f3f4f6', alignment: AlignmentType.RIGHT, size: 18 }),
          createCell(currentProvider, { bold: true, shading: 'fef2f2', alignment: AlignmentType.RIGHT, color: 'b91c1c', size: 18 }),
          createCell('Amplify HR', { bold: true, shading: 'eff6ff', alignment: AlignmentType.RIGHT, color: '1d4ed8', size: 18 }),
          createCell('Savings', { bold: true, shading: 'f0fdf4', alignment: AlignmentType.RIGHT, color: '166534', size: 18 }),
        ],
      });

      // Create data rows - Calculate using actual proposed rates (same as PDF)
      const dataRows = lineItems.map(item => {
        // Calculate current costs
        const c = calculate(item, sutaBases);
        const totalEmployees = currentTotals?.employees || 0;
        const currentAdminPerItem = totalEmployees > 0 
          ? (currentAdminFee * item.employeeCount / totalEmployees) 
          : 0;
        const currentItemTotal = c.wcPremium + c.sutaTax + currentAdminPerItem;
        
        // Find the matching proposed item with discounted rates
        const p = proposedItems.find(pi => pi.id === item.id);
        // Calculate proposed costs using the actual discounted rates
        const proposedItem = p || item;
        const pc = calculate(proposedItem, sutaBases);
        const proposedEmployees = proposedTotals?.employees || totalEmployees || 1;
        const proposedAdminPerItem = proposedEmployees > 0 
          ? (proposedAdminFee * item.employeeCount / proposedEmployees) 
          : 0;
        const proposedItemTotal = pc.wcPremium + pc.sutaTax + proposedAdminPerItem;
        const itemSavings = currentItemTotal - proposedItemTotal;
        
        return new TableRow({
          children: [
            createCell(item.state, { size: 18 }),
            createCell(item.wcCode, { size: 18 }),
            createCell(String(item.employeeCount), { alignment: AlignmentType.RIGHT, size: 18 }),
            createCell(fmt(item.grossWages), { alignment: AlignmentType.RIGHT, size: 18 }),
            createCell(fmt(currentItemTotal), { alignment: AlignmentType.RIGHT, shading: 'fefefe', size: 18 }),
            createCell(fmt(proposedItemTotal), { alignment: AlignmentType.RIGHT, shading: 'fafcff', size: 18 }),
            createCell(fmt(itemSavings), { alignment: AlignmentType.RIGHT, shading: 'fafdfb', color: itemSavings > 0 ? '166534' : itemSavings < 0 ? 'b91c1c' : '6b7280', size: 18 }),
          ],
        });
      });

      // Totals row
      const totalsRow = new TableRow({
        children: [
          createCell('TOTALS', { bold: true, shading: 'f3f4f6', size: 18 }),
          createCell('', { shading: 'f3f4f6', size: 18 }),
          createCell(String(currentTotals.employees || 0), { bold: true, alignment: AlignmentType.RIGHT, shading: 'f3f4f6', size: 18 }),
          createCell(fmt(currentTotals.grossWages || 0), { bold: true, alignment: AlignmentType.RIGHT, shading: 'f3f4f6', size: 18 }),
          createCell(fmt(currentTotal), { bold: true, alignment: AlignmentType.RIGHT, shading: 'fef2f2', color: 'b91c1c', size: 18 }),
          createCell(fmt(proposedTotal), { bold: true, alignment: AlignmentType.RIGHT, shading: 'eff6ff', color: '1d4ed8', size: 18 }),
          createCell(fmt(savings), { bold: true, alignment: AlignmentType.RIGHT, shading: 'f0fdf4', color: '166534', size: 18 }),
        ],
      });

      const comparisonTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [headerRow, ...dataRows, totalsRow],
      });
      children.push(comparisonTable);

      // Why Amplify HR Section
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'WHY AMPLIFY HR?', bold: true, size: 24, color: '1f2937', font: 'Calibri' })],
          spacing: { before: 500, after: 200 },
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'e5e7eb' } },
        }),
        new Paragraph({
          children: [new TextRun({ text: '• ', size: 20, color: '3c68ff' }), new TextRun({ text: 'IRS Certified Professional Employer Organization (CPEO)', size: 20, color: '374151', font: 'Calibri' })],
          spacing: { after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: '• ', size: 20, color: '3c68ff' }), new TextRun({ text: 'ESAC Accredited - Meeting the highest industry standards', size: 20, color: '374151', font: 'Calibri' })],
          spacing: { after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: '• ', size: 20, color: '3c68ff' }), new TextRun({ text: '4x Inc. 5000 Honoree - One of America\'s fastest-growing companies', size: 20, color: '374151', font: 'Calibri' })],
          spacing: { after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: '• ', size: 20, color: '3c68ff' }), new TextRun({ text: 'G2 High Performer - Top-rated by customers', size: 20, color: '374151', font: 'Calibri' })],
          spacing: { after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: '• ', size: 20, color: '3c68ff' }), new TextRun({ text: 'Forbes Recognized - Trusted by industry leaders', size: 20, color: '374151', font: 'Calibri' })],
          spacing: { after: 200 },
        })
      );

      // Create document with professional footer
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: { top: 1000, right: 1000, bottom: 1000, left: 1000 },
            },
          },
          children: children,
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  border: { top: { style: BorderStyle.SINGLE, size: 6, color: '3c68ff' } },
                  spacing: { before: 200 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'IRS CPEO Certified  •  ESAC Accredited  •  Inc. 5000 Honoree  •  G2 High Performer  •  Forbes Recognized', size: 16, color: '6b7280', font: 'Calibri' }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 100, after: 80 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'amplifyhrm.com', size: 16, color: '3c68ff', font: 'Calibri' }),
                    new TextRun({ text: '    •    ', size: 16, color: '9ca3af', font: 'Calibri' }),
                    new TextRun({ text: 'Confidential - Not for Distribution', size: 16, italics: true, color: 'dc2626', font: 'Calibri' }),
                    new TextRun({ text: '    •    ', size: 16, color: '9ca3af', font: 'Calibri' }),
                    new TextRun({ text: 'Amplify HR Pricing Engine', size: 16, color: '6b7280', font: 'Calibri' }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          },
        }],
      });

      // Generate and save
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${clientName || 'Quote'}_Amplify_HR_Proposal_${quoteDate}.docx`);
      setExportProgress('');
    } catch (error) {
      console.error('Word generation error:', error);
      setExportProgress('Error generating document');
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    generateExcel,
    generatePdf,
    generateWord,
    isExporting,
    exportProgress,
  };
}
